import * as React from 'react';
import { Row, Select } from 'antd';
import { Form } from '@ant-design/compatible';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FILTER_OPTIONS, getTagRender } from '../../utils/FilterUtils';

export function FilterOptionsWrapper(props) {
	const { filter, buildFilter, filtersStateSlice, updateFilters } = props;
	const { fieldName, filterOptions = [], handleChange, filtersValueAccessor } = filter;
	const [visible, setVisible] = useState(true);
	const contentRef = useRef<HTMLDivElement>(null);

	const initialFilterOption = useMemo(() => {
		if (!filtersValueAccessor || !filtersStateSlice) return FILTER_OPTIONS.IS;
		for (const option of filterOptions) {
			if (option.value === FILTER_OPTIONS.IS) continue;
			const values = filtersValueAccessor(filtersStateSlice, option.value);
			const hasValues = Array.isArray(values) ? values.length > 0 : !!values;
			if (hasValues) return option.value;
		}
		return FILTER_OPTIONS.IS;
	}, []);

	const [selectedFilterOption, setSelectedFilterOption] = useState(initialFilterOption);
	const prevOptionRef = useRef(initialFilterOption);

	const newFilter = useMemo(
		() => ({
			...filter,
			label: undefined,
			tagRender: getTagRender(selectedFilterOption),
			...(handleChange && filterOptions.length > 0
				? { handleChange: handleChange(selectedFilterOption) }
				: {}),
			selectOption: selectedFilterOption,
		}),
		[selectedFilterOption, filter]
	);

	const component = buildFilter(newFilter);
	useEffect(() => {
		if (!selectedFilterOption || !handleChange || filterOptions.length === 0) return;
		const prevOption = prevOptionRef.current;
		prevOptionRef.current = selectedFilterOption;
		if (prevOption === selectedFilterOption) return;
		const prevValues = filtersValueAccessor(filtersStateSlice, prevOption);
		const hasValues = Array.isArray(prevValues) ? prevValues.length > 0 : !!prevValues;
		if (hasValues) {
			handleChange(selectedFilterOption)(prevValues);
			if (updateFilters) {
				updateFilters({});
			}
		}
	}, [selectedFilterOption]);

	// Check if the rendered component produced any DOM content
	useEffect(() => {
		if (!contentRef.current) return;
		const observer = new MutationObserver(() => {
			const hasContent = contentRef.current && contentRef.current.childElementCount > 0;
			setVisible(hasContent);
		});
		// Check initial state
		setVisible(contentRef.current.childElementCount > 0);
		observer.observe(contentRef.current, { childList: true });
		return () => observer.disconnect();
	}, []);

	if (filterOptions.length === 0) {
		return component;
	}

	return (
		<div style={visible ? undefined : { display: 'none' }}>
			<Row className={'flex justify-between align-baseline'} align="middle">
				<span>{filter.label}</span>
				<Form.Item
					name={filter.fieldName.concat('_filterOptions')}
					style={{ marginBottom: 0, paddingBottom: 0 }}
				>
					<Select
						style={{ marginLeft: 8, flex: 0, minWidth: 75, textAlign: 'right' }}
						bordered={false}
						optionLabelProp="label"
						defaultValue={initialFilterOption}
						onSelect={(val) => {
							setSelectedFilterOption(val);
						}}
					>
						{filter.filterOptions.map((filterOption) => (
							<Select.Option
								key={filterOption.value}
								value={filterOption.value}
								label={filterOption.label}
							>
								{filterOption.label}
							</Select.Option>
						))}
					</Select>
				</Form.Item>
			</Row>
			<div ref={contentRef}>{component}</div>
		</div>
	);
}
